import { useEffect, useRef, useState } from 'react'

import { Box, Text, Touchable } from '@factorialco/deprecated-design-system'
import { F0Button, F0Link } from '@factorialco/f0-react'
import { Dropdown, RichTextDisplay, RichTextEditor } from '@factorialco/f0-react/dist/experimental'
import { Pencil } from '@factorialco/f0-react/icons/app'
import Ellipsis from '@factorialco/f0-react/icons/app/Ellipsis'
import {
  DeleteFlexibleTimeRecordCommentDocument,
  DeleteFlexibleTimeRecordCommentMutation,
  GlobalTimeRecordsDocument,
  UpdateFlexibleTimeRecordCommentDocument,
  UpdateFlexibleTimeRecordCommentMutation,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import type ProjectsStore from 'modules/project_management/store/models/Projects'

export interface Comment {
  id: number
  projectName: string
  subprojectName: string | undefined
  description: string
  time: string
  isFlexible: boolean
}
interface CommentCardProps {
  comment: Comment
  projectsStore: ProjectsStore
  weekStart: string
  weekEnd: string
}

const CommentCard = ({ comment, projectsStore, weekStart, weekEnd }: CommentCardProps) => {
  const [showFullText, setShowFullText] = useState<boolean>(false)
  const [newDescription, setNewDescription] = useState<string | null>(comment.description)
  const { editorConfig } = useRichTextEditor()
  const [hover, setHover] = useState(false)
  const [showEdit, setShowEdit] = useState<boolean>(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)
  const [showToggle, setShowToggle] = useState<boolean>(false)
  const textRef = useRef<HTMLDivElement>(null)

  const isFocused = hover || isDropdownOpen

  const refetchQueries = [
    {
      query: GlobalTimeRecordsDocument,
      variables: {
        startsOn: weekStart,
        endsOn: weekEnd,
        projectWorkerIds: projectsStore.projectWorkerIds,
      },
    },
  ]

  const [updateFlexibleTimeRecordComment] = useMutation(UpdateFlexibleTimeRecordCommentDocument, {
    extractorFn: (data: UpdateFlexibleTimeRecordCommentMutation) =>
      data.projectManagementMutations
        .updateByFlexibleTimeRecordProjectmanagementFlexibleTimeRecordComment,
    awaitRefetchQueries: true,
    refetchQueries: refetchQueries,
  })

  const [deleteFlexibleTimeRecordComment] = useMutation(DeleteFlexibleTimeRecordCommentDocument, {
    extractorFn: (data: DeleteFlexibleTimeRecordCommentMutation) =>
      data.projectManagementMutations
        .deleteByFlexibleTimeRecordProjectmanagementFlexibleTimeRecordComment,
    awaitRefetchQueries: true,
    refetchQueries: refetchQueries,
  })

  // Check if text exceeds 3 lines
  useEffect(() => {
    const timer = setTimeout(() => {
      if (textRef.current) {
        const element = textRef.current
        const richTextElement = element.querySelector('[class*="rich-text"]')

        if (richTextElement) {
          const originalClass = richTextElement.className
          richTextElement.className = originalClass.replace('line-clamp-3', '')

          const lineHeight = parseInt(window.getComputedStyle(element).lineHeight)
          const maxHeight = lineHeight * 3
          const shouldShow = element.scrollHeight > maxHeight

          richTextElement.className = originalClass

          setShowToggle(shouldShow)
        }
      }
    }, 0)

    return () => clearTimeout(timer)
  }, [newDescription, showFullText, comment.description])

  const handleMouseEnter = () => {
    setHover(true)
  }

  const handleMouseLeave = () => {
    if (!isDropdownOpen) {
      setHover(false)
    }
  }

  const onSaveChanges = (description: string | null) => {
    track(trackingEvents.projectManagement.myProjects.commentsSidepanel.clickOnSaveComment)
    if (!description) {
      deleteFlexibleTimeRecordComment({
        variables: {
          flexibleTimeRecordId: comment.id,
        },
      })
    } else {
      updateFlexibleTimeRecordComment({
        variables: {
          flexibleTimeRecordId: comment.id,
          content: description,
        },
      })
    }
  }

  const commentTitle = comment.subprojectName
    ? comment.projectName + ' -> ' + comment.subprojectName
    : comment.projectName

  return (
    <Box paddingBottom="s6">
      <Box
        background={(hover || showEdit) && comment.isFlexible ? 'grey200' : 'white'}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        borderRadius={{ all: 'abs008' }}
        width="full"
        paddingTop="s12"
        paddingX="s16"
        paddingBottom="s12"
      >
        <Box flexDirection="row" justifyContent="spaceBetween">
          <Box flexDirection="column" gap="s6" paddingBottom="s6">
            <Text.Normal size="200" weight="bold" color="grey1000">
              {commentTitle}
            </Text.Normal>
            <Text.Normal size="200" weight="medium" color="grey700">
              {i18n.t('common.decimal_to_time.hours.basic.hour_amount', {
                total: comment.time,
              })}
            </Text.Normal>
          </Box>
          <Box width="s32" height="s24">
            {isFocused && comment.isFlexible && (
              <Dropdown
                open={isDropdownOpen}
                items={[
                  {
                    label: i18n.t('common.edit'),
                    icon: Pencil,
                    onClick: () => {
                      setShowEdit(!showEdit)
                      setHover(false)
                      track(
                        trackingEvents.projectManagement.myProjects.commentsSidepanel
                          .clickOnEditComment
                      )
                    },
                  },
                ].filter(Boolean)}
                onOpenChange={(open) => {
                  setIsDropdownOpen(open)
                  if (!open && !hover) {
                    setHover(false)
                  }
                }}
              >
                <F0Button
                  type="secondary"
                  icon={Ellipsis}
                  variant="ghost"
                  label={i18n.t('common.more')}
                  hideLabel
                />
              </Dropdown>
            )}
          </Box>
        </Box>
        {showEdit ? (
          <RichTextEditor
            {...editorConfig}
            primaryAction={{
              action: {
                label: i18n.t('common.save'),
                onClick: () => {
                  setShowEdit(false)
                  onSaveChanges(newDescription)
                },
                variant: 'default',
              },
            }}
            title={commentTitle}
            onChange={(data) => {
              setNewDescription(data?.value ?? '')
            }}
            placeholder={comment.description}
            initialEditorState={{ content: newDescription ?? '' }}
            plainHtmlMode={false}
          />
        ) : (
          <>
            <div ref={textRef}>
              <RichTextDisplay
                content={newDescription || ''}
                className={showFullText ? '' : 'line-clamp-3'}
              />
            </div>
            {showToggle && (
              <Touchable onClick={() => setShowFullText(!showFullText)}>
                <F0Link>
                  {showFullText ? i18n.t('common.see_less') : i18n.t('common.see_more')}
                </F0Link>
              </Touchable>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}

export default CommentCard
